// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file useClickableContainer.ts
 * @input Container ref, interactive element ref, click/href handlers
 * @output onClick and onMouseUp handlers for the container; INTERACTIVE_SELECTORS list
 * @position Core hook for clickable containers that safely handle nested interactive elements
 *
 * Solves the "nested interactive elements" problem: when a card is clickable
 * but contains buttons/links, clicking those should NOT trigger the card's action.
 *
 * SYNC: When modified, update:
 * - /packages/core/src/hooks/index.ts (export)
 */
import { useCallback, useEffect } from 'react';

/**
 * Canonical list of interactive element selectors — native controls plus
 * role-based interactive elements. Clicks on these (or their descendants)
 * should NOT bubble to a clickable container's click handler.
 *
 * Exported so other focus/interaction utilities can share one comprehensive
 * definition of "interactive target" rather than hand-rolling divergent lists.
 * Note: this list is about "don't bubble clicks", not focus-eligibility —
 * consumers that need focusable elements must additionally exclude
 * unfocusable/disabled targets (e.g. `[tabindex="-1"]`, `:disabled`).
 */
export const INTERACTIVE_SELECTORS = ['button', 'a', 'input', 'select', 'textarea', '[role="button"]', '[role="link"]', '[role="checkbox"]', '[role="radio"]', '[role="switch"]', '[role="tab"]', '[role="menuitem"]', '[role="option"]', '[role="combobox"]', '[role="listbox"]', '[role="slider"]', '[role="spinbutton"]', '[data-pressable-container]'].join(',');
const NON_INTERACTIVE_SELECTORS = '[aria-readonly="true"]';

/**
 * Check whether an element has an interactive ancestor between it and the root.
 * If the click target is inside a nested button/link/etc., we should NOT
 * handle it at the container level.
 */
function hasInteractiveAncestor(el, rootEl) {
  let current = el;
  while (current != null && current !== rootEl && current !== document.body) {
    if (current.matches(INTERACTIVE_SELECTORS) && !current.matches(NON_INTERACTIVE_SELECTORS)) {
      return true;
    }
    current = current.parentElement;
  }
  return false;
}

/** Check if there's a text selection inside the node (don't navigate on text select) */
function hasTextSelection(node) {
  if (typeof document === 'undefined' || !('getSelection' in document)) {
    return false;
  }
  const selection = document.getSelection();
  if (selection == null || selection.isCollapsed) {
    return false;
  }
  return node.contains(selection.anchorNode);
}
/**
 * Hook that makes a container element clickable while preserving
 * nested interactive element behavior.
 *
 * When the user clicks the container surface (not on a nested button/link),
 * the hook fires the onClick handler or navigates to href.
 * When the user clicks a nested interactive element, it does nothing —
 * the nested element handles its own event.
 *
 * @compositionHint Use inside ClickableCard or SelectableCard.
 * For custom interactive containers, pair with a ref to the outer div.
 *
 * @example
 * ```
 * const containerRef = useRef<HTMLDivElement>(null);
 * const { onClick, onMouseUp } = useClickableContainer({
 *   containerRef,
 *   onClick: () => console.log('card clicked'),
 * });
 * return (
 *   <div ref={containerRef} onClick={onClick} onMouseUp={onMouseUp}>
 *     <p>Click anywhere on this card</p>
 *     <button onClick={() => alert('button')}>Nested button</button>
 *   </div>
 * );
 * ```
 */
export function useClickableContainer({
  containerRef,
  interactiveRef,
  onClick: onClickProp,
  href,
  target,
  disabled = false
}) {
  // Mark container as pressable for the interactive selector check
  useEffect(() => {
    const el = containerRef.current;
    if (el) {
      el.setAttribute('data-pressable-container', 'true');
    }
  }, [containerRef]);
  const onClick = useCallback(event => {
    if (disabled) {
      return;
    }
    const containerEl = containerRef.current;
    if (!containerEl) {
      return;
    }

    // Don't trigger on text selection
    if (hasTextSelection(containerEl)) {
      return;
    }
    const eventTarget = event.target;
    if (!(eventTarget instanceof Element)) {
      return;
    }

    // If the click landed on or inside a nested interactive element, bail
    if (eventTarget !== event.currentTarget && hasInteractiveAncestor(eventTarget, containerEl)) {
      return;
    }

    // Fire the click handler
    onClickProp?.(event);
    if (event.defaultPrevented) {
      return;
    }

    // Navigate if href is provided
    if (href != null) {
      const shouldOpenNewTab = target === '_blank' || event.ctrlKey || event.metaKey;
      if (shouldOpenNewTab) {
        window.open(href, '_blank', 'noopener');
      } else if (interactiveRef?.current) {
        // Proxy click to the sr-only link so the framework link component
        // handles navigation (client-side transitions in Next.js, etc.).
        interactiveRef.current.click();
      } else {
        // eslint-disable-next-line react-compiler/react-compiler -- browser navigation API
        window.location.href = href;
      }
    }

    // Proxy click to the interactive ref if no explicit handler
    if (href == null && onClickProp == null && interactiveRef?.current) {
      const clickEvent = new MouseEvent('click', {
        bubbles: event.bubbles,
        cancelable: event.cancelable,
        ctrlKey: event.ctrlKey,
        metaKey: event.metaKey,
        shiftKey: event.shiftKey,
        altKey: event.altKey,
        button: event.button
      });
      interactiveRef.current.dispatchEvent(clickEvent);
      event.stopPropagation();
    }
  }, [containerRef, interactiveRef, onClickProp, href, target, disabled]);
  const onMouseUp = useCallback(event => {
    if (disabled) {
      return;
    }
    const containerEl = containerRef.current;
    if (!containerEl) {
      return;
    }
    const eventTarget = event.target;
    if (!(eventTarget instanceof Element)) {
      return;
    }

    // Middle-click on href opens in new tab
    const isMiddleClick = event.button === 1;
    if (isMiddleClick && href != null && (eventTarget === event.currentTarget || !hasInteractiveAncestor(eventTarget, containerEl))) {
      window.open(href, '_blank', 'noopener');
    }
  }, [containerRef, href, disabled]);
  return {
    onClick,
    onMouseUp
  };
}